"use client";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { getPassengerDetailsAction } from "../../Actions/LogicActions/Checkout/PassengerDetails/GetPassengerDetailsAction";
import { resetManageSeatingState } from "../../Actions/UIActions/ManageSeating/ResetManageSeatingStateAction";
import { useAuth } from "../../Auth/useAuth";
import {
  bookingTree,
  evtTree,
  myAccountTree,
} from "../../Orchestrator/ConstantTree";
import { clearAllWhitelistedAtomsFromStorage, useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { IPreferredLanguage, IBookingResponse, IUserResponse } from "../../Orchestrator/Types/Index";
import { _eventBus } from "../../core/events/EventBus";
import { flagSearchFlowNavigation } from "../../Lib/Utils/searchFlowNavigation";
import { useLocale } from "../Context/LocaleContext";
import useNetworkStatus from "../CustomHooks/useNetworkStatus";
import { closeLanguageDropdownAction } from "../../Actions/UIActions/TranslationLanguage/CloseLanguageDropdownAction";
import BottomBar from "./Bottombar";
import { FlowPersistence } from "../../core/flows/FlowPersistence";
import { getUserDetailsAction } from "../../Actions/LogicActions/MyAccount/GetUserDetailsAction";
import { DEFAULT_LOCALE } from "../../config/global";
import { Button } from "antd";
const menuItems: string[] = [
  "search",
  "bookings",
  "offers",
  "networks-maps-stations",
  // "journey-planner", // hidden from nav
  "destinations",
  // "network_maps",
  // "stations",
  // "journey_planner",
   "live-schedule",
];

export function TopBar({
  children,
}: {
  children?: React.ReactNode | React.ReactNode[];
}) {
  const intl = useIntl();
  const router = useRouter();
  const { login, logout } = useAuth();
  const status = useNetworkStatus();
  const pathname = usePathname() || "";
  const [showHeader, setShowHeader] = useState(true);
  const [isBottomBarSticky, setIsBottomBarSticky] = useState(false);
  const [bottomBarHeight, setBottomBarHeight] = useState<number>(0);
  const bottomBarRef = useRef<HTMLDivElement | null>(null);
  const initialBottomBarTopRef = useRef<number | null>(null);
  const [languages] = useRecState<IPreferredLanguage[]>(
    "languagesResponseAtom"
  );
  const [languageDropdownOpen] = useRecState<boolean>(
    "languageDropdownOpenAtom"
  );

  const { locale: localeLanguage, setLocale } = useLocale();

  useEffect(() => {
    setLocale(localeLanguage);
  }, [localeLanguage]);

  useEffect(() => {
    setTimeout(() => {
      _eventBus.emit({
        eventName: evtTree.TopBar.getCmsAppDownloadLinkAndQr,
      })
      _eventBus.emit({
        eventName: evtTree.TopBar.Init,
        data: { langCode: localeLanguage, router }
      })
    }, 500)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (languageDropdownOpen && !target.closest('#language-toggle')) {
        closeLanguageDropdownAction.execute();
        _eventBus.emit({
          eventName: evtTree.TopBar.SelectLanguage.Close
        });
      }
    };

    if (languageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [languageDropdownOpen]);

  const currentLanguageText =
    localeLanguage === "en"
      ? intl.formatMessage(
        { id: "language_toggle_english" },
        { defaultMessage: "English" }
      )
      : localeLanguage === "ar"
        ? intl.formatMessage(
          { id: "language_toggle_arabic" },
          { defaultMessage: "Arabic" }
        )
        : intl.formatMessage(
          { id: "language_toggle_french" },
          { defaultMessage: "French" }
        );
  const [isAuthenticated] = useRecState<boolean>("isAuthenticatedStateAtom");
  const [isAnonymous] = useRecState<boolean>("isAnonymousStateAtom");
  const [authLoaded] = useRecState<boolean>("authLoadedStateAtom");
  const [seatsChangedOnCheckout] = useRecState<boolean>("seatsChangedOnCheckoutAtom");
  const [booking] = useRecState<IBookingResponse>("bookingResponseAtom");
  const [userData] = useRecState<IUserResponse>("userDataAtom");

  const userInitials = (() => {
    const cleanName = (n?: string) =>
      n && n !== "-" ? n.trim() : "";
    const initialsFor = (n: string) =>
      cleanName(n)
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase())
        .join("");
    return `${initialsFor(userData?.firstName ?? "")}${initialsFor(userData?.lastName ?? "")}`;
  })();

  const handleAuthAction = () => {
    _eventBus.emit({
      eventName: evtTree.TopBar.Login.Click,
      data: { isAuthenticated, login: () => login() },
    });

    _eventBus.emit({
      eventName: myAccountTree.MyAccount.Action.Click,
      data: "myAccount",
    });
  };

  // useEffect(() => {
  //   topBarStartFlow();
  // }, []);

  useEffect(() => {
    if (isAuthenticated) {
      //Trigger API to get current user logged details if user refresh the page.
      //   userCustomerService.fetchUserData();
      // getUserDetailsAction.execute();
      getPassengerDetailsAction.execute();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Watch for pathname changes (route changes)
    if (
      pathname.includes("/checkout") ||
      pathname.includes("/train-offer") ||
      pathname.includes("/shuttle") ||
      pathname.includes("/fmlm-offer") ||
      pathname.includes("/multi-booking")
    ) {
      setShowHeader(false);
      setIsBottomBarSticky(false);
      initialBottomBarTopRef.current = null;
    } else {
      setShowHeader(true);
    }
  }, [pathname]); // Dependency array ensures this runs on pathname change.

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!showHeader || !bottomBarRef.current) {
      setIsBottomBarSticky(false);
      return;
    }

    const updateInitialPosition = () => {
      if (typeof window === 'undefined' || !bottomBarRef.current) {
        return;
      }
      
      const rect = bottomBarRef.current.getBoundingClientRect();
      if (rect) {
        initialBottomBarTopRef.current = rect.top + window.scrollY;
        setBottomBarHeight(rect.height);
      }
    };

    const timeoutId = setTimeout(() => {
      updateInitialPosition();
    }, 100);

    const handleScroll = () => {
      if (typeof window === 'undefined' || !bottomBarRef.current || initialBottomBarTopRef.current === null) {
        return;
      }

      const currentScrollY = window.scrollY;
      
      if (currentScrollY >= (initialBottomBarTopRef.current)) {
        setIsBottomBarSticky(true);
      } 
      else if (currentScrollY < initialBottomBarTopRef.current) {
        setIsBottomBarSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateInitialPosition);

    return () => {
      if (typeof window !== 'undefined') {
        clearTimeout(timeoutId);
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", updateInitialPosition);
      }
    };
  }, [showHeader]);

  const handleSelectLanguage = async (langCode: string) => {
    _eventBus.emit({
      eventName: evtTree.TopBar.SelectLanguage.Select,
      data: { langCode: langCode.toLowerCase(), router },
    });
    sessionStorage.setItem("lang", langCode);
  };

  // useEffect(() => {
  //   handleSelectLanguage(DEFAULT_LOCALE);
  // }, []);

  return (
    // <div className="header" style={{ backgroundColor: "#F3E6DD" }}>
    // <div className="header">
    <div
      className={`header ${pathname.includes("/train-offer") || pathname.includes("/checkout") || pathname.includes("/multi-booking") && !pathname.includes("callback")
        ? "header-custom"
        : ""
        }`}
    >
      <div className="container">
        <div className="site-main-header flex">
          <div className="logo">
            <Image
              src="/images/white_logo.svg"
              width={176}
              height={34}
              alt="logo"
              className="cursor-pointer"
              onClick={async () => {
                if (pathname.includes("/checkout") && seatsChangedOnCheckout && booking?.booking?.id) {
                  _eventBus.emit({
                    eventName: evtTree.TopBar.Logo.CancelPlaceSelection,
                    data: { bookingId: booking.booking.id },
                  });
                }

                FlowPersistence.cleanup();
                await clearAllWhitelistedAtomsFromStorage();

                // Close and reset manage seating context to avoid stale state
                resetManageSeatingState();
                _eventBus.emit({
                  eventName: bookingTree.ManageSeating.Flow.Close,
                });
                // The ROUTE_CHANGE to "/search" below is async, so the HomePageFlow
                // restart triggered by FLOW_CHANGE can run while window.location.pathname
                // is still "/train-offer". Flag the intent so MainBookingInit mounts the
                // SearchFlow (and its Search-button subscription) instead of the offer flows.
                flagSearchFlowNavigation();
                _eventBus.emit({
                  eventName: "FLOW_CHANGE",
                  data: "HomePageFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "AfterSalesBookingFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "FmlmViewFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "CheckoutFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "SeatingMapFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "RequestCorporateAccountFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "NetworkMapStationFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "RequestCorporateAccountFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "UpgradeBookingFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "AddShuttleTransferFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "GroupBookingFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "PlanMultiBookFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "MultiBookingFlow",
                });
                _eventBus.emit({
                  eventName: "FLOW_CLOSE",
                  data: "AddShuttleTransferCleanup",
                });

                if (isAuthenticated) {
                  _eventBus.emit({
                    eventName: "ROUTE_CHANGE",
                    data: "/search",
                  });
                } else {
                  _eventBus.emit({
                    eventName: "ROUTE_CHANGE",
                    data: "/",
                  });
                }

                _eventBus.emit({
                  eventName: bookingTree.QuickExchange.Cancel,
                });
              }}
            />
          </div>

          <div className="header-right">
            <div
              className="cursor-pointer d-flex align-items-center"
              id="language-toggle"
              onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.className.includes("language-target")) {
                  _eventBus.emit({
                    eventName: "TopBar.SelectLanguage.Click",
                  });
                }
              }}
            >
              <Image
                src={
                  localeLanguage === "en"
                    ? "/images/uk_flag.png"
                    : localeLanguage === "ar"
                      ? "/images/arabic_flag.png"
                      : "/images/french_flag.png"
                }
                width={24}
                height={24}
                alt="language"
                className="language-target"
              />
              <span className="language-text language-target">
                {currentLanguageText}
              </span>
              {languageDropdownOpen && (
                <div
                  className="language-dropdown"
                  dir={localeLanguage === "ar" ? "rtl" : "ltr"}
                  aria-label="language options"
                >
                  {languages.map((lang: IPreferredLanguage, index: number) => (
                    <div
                      key={index}
                      className="language-option"
                      onClick={() => handleSelectLanguage(lang.value)}
                    >
                      {lang.label === "English"
                        ? lang.label
                        : intl.formatMessage({ id: lang.label })}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Link href="/help" className="unset-link-style cont-info">
              <Image
                src={"/search/question-circle.svg"}
                width={24}
                alt="contact us"
                height={24}
              />
              <span>
                {/* {intl.formatMessage({ id: "contact_us" })} */}
                {intl.formatMessage({ id: "help" })}
              </span>
            </Link>

            <div className="account-info">
              <button className="auth-button" onClick={handleAuthAction}>
                <Image
                  src={"/search/user-icon.svg"}
                  width={16}
                  alt="account"
                  height={20}
                />
                <span
                  className={`auth-button-text${
                    authLoaded && isAuthenticated && !isAnonymous && userInitials
                      ? " auth-button-text--initials"
                      : ""
                  }`}
                >
                  {authLoaded ? (
                    isAuthenticated && !isAnonymous ? (
                      userInitials || intl.formatMessage({ id: "account" })
                    ) : (
                      intl.formatMessage({ id: "sign_in_register" })
                    )
                  ) : (
                    <span className="loading-text">&nbsp;</span>
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showHeader && (
        <>
          <BottomBar ref={bottomBarRef} menuItems={menuItems} isSticky={isBottomBarSticky} />
          {isBottomBarSticky && bottomBarHeight > 0 && (
            <div 
              style={{ 
                height: `${bottomBarHeight}px`,
                width: '100%'
              }} 
              aria-hidden="true"
            />
          )}
        </>
      )}
    </div>
  );
}
